import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Columns3,
  Download,
  Filter,
  FilterX,
  Maximize2,
  Minimize2,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { ColumnsDrawer } from "../../table/ColumnsDrawer";
import { getActiveCountFromCriteria } from "./helpers";
import { useFilterDraft } from "./useFilterDraft";
import { useFilterPresets } from "./useFilterPresets";
import { ActiveFilterChips } from "./components/ActiveFilterChips";
import { FilterFieldsPanel } from "./components/FilterFieldsPanel";
import { PresetsPanel } from "./components/PresetsPanel";
import { SearchBox } from "./components/SearchBox";
import type { FilterBarProps } from "./types";

export function FilterBar<
  F extends Record<string, any> = Record<string, any>,
  C extends Record<string, any> = Record<string, any>,
>({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Buscar...",
  onClearAll,
  filters,
  filtrosCriteria,
  setFiltrosCriteria,
  columns,
  onColumnsChange,
  actionButton,
  isFullscreen,
  onToggleFullscreen,
  showActiveFilters = true,
  filterPresets = {},
  storageKey,
  onExport,
  exportTitle = "Exportar CSV",
  totalCount,
}: FilterBarProps<F, C>) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isColumnsDrawerOpen, setIsColumnsDrawerOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");

  const draft = useFilterDraft<C>({
    filters,
    filtrosCriteria,
    searchValue,
    drawerOpen,
    setFiltrosCriteria,
  });
  const presets = useFilterPresets(storageKey);

  const activeCount = useMemo(() => getActiveCountFromCriteria(filtrosCriteria), [filtrosCriteria]);

  const handleApply = useCallback(() => {
    draft.apply();
    setDrawerOpen(false);
  }, [draft]);

  const handleRemoveSpecificFilter = useCallback(
    (group: string, valueToRemove?: any) => {
      if (!filtrosCriteria) return;

      const updatedCriteria = { ...filtrosCriteria } as any;

      if (valueToRemove !== undefined) {
        if (Array.isArray(updatedCriteria[group])) {
          updatedCriteria[group] = updatedCriteria[group].filter((v: any) => v !== valueToRemove);
          if (updatedCriteria[group].length === 0) {
            delete updatedCriteria[group];
          }
        }
      } else {
        delete updatedCriteria[group];
      }

      setFiltrosCriteria?.(updatedCriteria);
    },
    [filtrosCriteria, setFiltrosCriteria],
  );

  const handleClearAll = useCallback(() => {
    draft.reset();
    setFiltrosCriteria?.({} as C);
    onClearAll?.();
    setDrawerOpen(false);
    toast.success("Filtros limpos!");
  }, [draft, setFiltrosCriteria, onClearAll]);

  const handleApplyPreset = useCallback(
    (presetCriteria: Record<string, any>) => {
      setFiltrosCriteria?.({ ...filtrosCriteria, search: searchValue, ...presetCriteria });
      setDrawerOpen(false);
      toast.success("Preset aplicado!");
    },
    [searchValue, setFiltrosCriteria, filtrosCriteria],
  );

  const handleSavePreset = useCallback(
    (name: string) => presets.save(name, filtrosCriteria),
    [presets, filtrosCriteria],
  );

  const hasSystemPresets = Object.keys(filterPresets).length > 0;
  const showPresetsUI = hasSystemPresets || presets.enabled;
  const totalPresets = Object.keys(filterPresets).length + Object.keys(presets.presets).length;

  // Ferramentas secundárias da barra: inline em telas largas, colapsadas num menu
  // "overflow" no mobile. Fonte única para os dois modos (sem duplicar a lógica).
  type SecondaryTool = { key: string; icon: ReactNode; label: string; onSelect: () => void };
  const secondaryTools: SecondaryTool[] = [
    ...(columns && columns.length > 0
      ? [
          {
            key: "columns",
            icon: <Columns3 className="h-4 w-4" />,
            label: "Gerenciar colunas",
            onSelect: () => setIsColumnsDrawerOpen(true),
          },
        ]
      : []),
    ...(onToggleFullscreen
      ? [
          {
            key: "fullscreen",
            icon: isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            ),
            label: isFullscreen ? "Sair da tela cheia" : "Expandir",
            onSelect: onToggleFullscreen,
          },
        ]
      : []),
    ...(onExport
      ? [
          {
            key: "export",
            icon: <Download className="h-4 w-4" />,
            label: exportTitle,
            onSelect: onExport,
          },
        ]
      : []),
  ];

  return (
    <div className="w-full space-y-2">
      <div className="flex flex-col items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 shadow-sm md:flex-row">
        <div className="flex w-full max-w-md flex-1 items-center gap-3">
          <SearchBox
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
          {totalCount !== undefined && (
            <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
              {totalCount.toLocaleString("pt-BR")} resultado{totalCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-nowrap">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-10 w-10 rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive",
              activeCount > 0 && "text-destructive",
            )}
            onClick={handleClearAll}
            title="Limpar todos os filtros"
          >
            <FilterX className="h-4 w-4" />
          </Button>

          {filters && (
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "gap-2 border-dashed transition-all hover:border-solid",
                    activeCount > 0 && "border-primary/40 bg-primary/5 text-primary",
                  )}
                >
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Filtros</span>
                  {activeCount > 0 && (
                    <Badge
                      variant="default"
                      className="ml-1 h-5 min-w-[1.25rem] justify-center rounded-full px-1 text-[10px]"
                    >
                      {activeCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>

              <SheetContent className="flex h-full w-full flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-2xl">
                <SheetHeader className="z-10 border-b border-border bg-background px-6 py-4">
                  <SheetTitle className="flex items-center gap-2 text-lg text-foreground">
                    <Filter className="h-5 w-5 text-primary" />
                    Filtros Avançados
                  </SheetTitle>
                </SheetHeader>

                {showPresetsUI ? (
                  <Tabs defaultValue="custom" className="flex flex-1 flex-col overflow-hidden">
                    <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-border bg-transparent p-0 px-6">
                      <TabsTrigger
                        value="custom"
                        className="-mb-px rounded-none border-b-2 border-transparent bg-transparent px-1 py-3 text-sm font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                      >
                        Personalizado
                      </TabsTrigger>

                      <TabsTrigger
                        value="presets"
                        className="-mb-px flex items-center gap-2 rounded-none border-b-2 border-transparent bg-transparent px-1 py-3 text-sm font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                      >
                        Presets
                        {totalPresets > 0 && (
                          <Badge
                            variant="secondary"
                            className="h-5 min-w-[1.25rem] justify-center rounded-full px-1 text-[10px]"
                          >
                            {totalPresets}
                          </Badge>
                        )}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="custom" className="flex-1 overflow-y-auto">
                      <FilterFieldsPanel
                        filters={filters}
                        selected={draft.selected}
                        dateRanges={draft.dateRanges}
                        searches={draft.searches}
                        fieldSearch={fieldSearch}
                        onFieldSearchChange={setFieldSearch}
                        onToggle={draft.toggle}
                        onBulkToggle={draft.bulkToggle}
                        onSearchChange={draft.changeSearch}
                        onDateChange={draft.changeDate}
                        onYearChange={draft.changeYear}
                      />
                    </TabsContent>

                    <TabsContent value="presets" className="flex-1 overflow-y-auto">
                      <PresetsPanel
                        systemPresets={filterPresets}
                        userPresets={presets.presets}
                        canPersist={presets.enabled}
                        appliedCriteria={filtrosCriteria}
                        activeCount={activeCount}
                        onApply={handleApplyPreset}
                        onSave={handleSavePreset}
                        onDelete={presets.remove}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div className="flex-1 overflow-y-auto">
                    <FilterFieldsPanel
                      filters={filters}
                      selected={draft.selected}
                      dateRanges={draft.dateRanges}
                      searches={draft.searches}
                      fieldSearch={fieldSearch}
                      onFieldSearchChange={setFieldSearch}
                      onToggle={draft.toggle}
                      onBulkToggle={draft.bulkToggle}
                      onSearchChange={draft.changeSearch}
                      onDateChange={draft.changeDate}
                      onYearChange={draft.changeYear}
                    />
                  </div>
                )}

                <SheetFooter className="flex-row gap-3 border-t border-border bg-background p-4 sm:justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={draft.reset}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Limpar
                  </Button>

                  <Button
                    onClick={handleApply}
                    size="sm"
                    className="min-w-[140px] shadow-lg shadow-primary/20"
                  >
                    Aplicar
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          )}

          {/* Drawer de colunas: sempre montado; o gatilho vive inline (desktop) ou no menu (mobile). */}
          {columns && columns.length > 0 && (
            <ColumnsDrawer
              open={isColumnsDrawerOpen}
              onClose={setIsColumnsDrawerOpen}
              columns={columns}
              onColumnsChange={onColumnsChange}
            />
          )}

          {secondaryTools.length > 0 && (
            <>
              {/* Desktop: ferramentas inline */}
              <div className="hidden items-center gap-2 sm:flex">
                {secondaryTools.map((tool) => (
                  <Button
                    key={tool.key}
                    variant="outline"
                    size="sm"
                    className="border-dashed text-muted-foreground hover:border-solid hover:text-foreground"
                    onClick={tool.onSelect}
                    title={tool.label}
                  >
                    {tool.icon}
                  </Button>
                ))}
              </div>

              {/* Mobile: menu "overflow" para não estourar a barra */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    buttonVariants({ variant: "outline", size: "sm" }),
                    "border-dashed text-muted-foreground sm:hidden",
                  )}
                  title="Mais opções"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {secondaryTools.map((tool) => (
                    <DropdownMenuItem key={tool.key} onClick={tool.onSelect} className="gap-2">
                      {tool.icon}
                      {tool.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {actionButton}
        </div>
      </div>

      {showActiveFilters && filters && filtrosCriteria && activeCount > 0 && (
        <ActiveFilterChips
          filters={filters}
          criteria={filtrosCriteria}
          onRemoveFilter={handleRemoveSpecificFilter}
        />
      )}
    </div>
  );
}
